import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Sparkles, ChevronRight, Loader2 } from "lucide-react";
import { ActivityThoughtRow } from "./ActivityThoughtRow";
import { ActivityActionRow } from "./ActivityActionRow";
import { isToolHidden, getToolMergeTarget, formatToolDisplayName } from "./formatToolDisplayName";

export const AgentActivityTimeline = ({
  steps = [],
  thoughts = [],
  turnElapsedMs = null,
  isStreaming = false,
  isStopped = false,
  startedAt = null,
  hasAnswer = false,
  showSteps = true,
  t,
}) => {
  const userToggledRef = useRef(false);

  // If showSteps is true: open while streaming before answer arrives.
  // If showSteps is false: stays collapsed while streaming.
  const [isOpen, setIsOpen] = useState(() => Boolean(showSteps && isStreaming && !hasAnswer));

  // Sync with showSteps when streaming without answer and user hasn't manually clicked
  useEffect(() => {
    if (isStreaming && !hasAnswer && !userToggledRef.current && !isStopped) {
      setIsOpen(Boolean(showSteps));
    }
  }, [showSteps, isStreaming, hasAnswer, isStopped]);

  // Auto-collapse when answer text starts streaming unless user explicitly toggled the timeline
  useEffect(() => {
    if (isStreaming && hasAnswer && !userToggledRef.current) {
      setIsOpen(false);
    }
  }, [isStreaming, hasAnswer]);

  const handleToggle = () => {
    userToggledRef.current = true;
    setIsOpen((prev) => !prev);
  };

  // Filter and process visible steps (hide internal plumbing like whoami, fold get_task_result)
  const visibleSteps = useMemo(() => {
    if (!Array.isArray(steps) || steps.length === 0) return [];

    const result = [];

    steps.forEach((s) => {
      if (!s || typeof s !== "object") return;
      const toolName = s.tool_name || s.name || "";

      // Hide internal tools like whoami
      if (isToolHidden(toolName)) return;

      // Check if tool merges into an earlier tool (e.g. get_task_result -> query_research)
      const mergeTarget = getToolMergeTarget(toolName);
      if (mergeTarget) {
        // If the merged task completed or failed, update the parent task's status
        const parent = result.slice().reverse().find(
          (item) => (item.tool_name || item.name) === mergeTarget
        );
        if (parent) {
          if (s.status === "error" || s.status === "timeout") {
            parent.status = s.status;
            parent.error_message = s.error_message;
          } else if (s.status === "success" && parent.status === "running") {
            parent.status = "success";
          }
        }
        return;
      }

      result.push({ ...s });
    });

    return result;
  }, [steps]);

  // Combine thoughts and visible action steps into a unified timeline grouped by iteration
  const timelineGroups = useMemo(() => {
    const groupsMap = new Map();

    // Attach thoughts by step number
    if (Array.isArray(thoughts)) {
      thoughts.forEach((th) => {
        if (!th || typeof th !== "object") return;
        const stepNum = th.step || 1;
        if (!groupsMap.has(stepNum)) {
          groupsMap.set(stepNum, { stepNum, thought: th, actions: [] });
        } else {
          groupsMap.get(stepNum).thought = th;
        }
      });
    }

    // Attach action steps by step number
    visibleSteps.forEach((s, idx) => {
      const stepNum = s.step || 1;
      if (!groupsMap.has(stepNum)) {
        groupsMap.set(stepNum, { stepNum, thought: null, actions: [] });
      }
      groupsMap.get(stepNum).actions.push({ step: s, globalIdx: idx });
    });

    return Array.from(groupsMap.values()).sort((a, b) => a.stepNum - b.stepNum);
  }, [thoughts, visibleSteps]);

  // Client-side live timer while streaming (Bug 8, P2)
  const [elapsedSeconds, setElapsedSeconds] = useState(() => {
    const start = startedAt || Date.now();
    return Math.max(1, Math.round((Date.now() - start) / 1000));
  });

  useEffect(() => {
    if (!isStreaming || isStopped) return;
    const start = startedAt || Date.now();
    const updateElapsed = () => {
      setElapsedSeconds(Math.max(1, Math.round((Date.now() - start) / 1000)));
    };
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [isStreaming, isStopped, startedAt]);

  // Step count matches what is rendered in timeline (Bug 9)
  const renderedStepsCount = Math.max(
    Array.isArray(thoughts) ? thoughts.length : 0,
    visibleSteps.length,
  );
  const hasItems = timelineGroups.length > 0 || renderedStepsCount > 0;
  if (!hasItems && !isStreaming && !isStopped) return null;

  // Header timing: prefer wall-clock turnElapsedMs when provided
  let formattedDuration = null;
  if (typeof turnElapsedMs === "number" && turnElapsedMs > 0) {
    formattedDuration = (turnElapsedMs / 1000).toFixed(1);
  } else if (!isStreaming) {
    const totalDurationMs = visibleSteps.reduce(
      (acc, s) =>
        acc +
        (typeof s.execution_time_ms === "number"
          ? s.execution_time_ms
          : typeof s.duration_ms === "number"
          ? s.duration_ms
          : 0),
      0,
    );
    if (totalDurationMs > 0) {
      formattedDuration = (totalDurationMs / 1000).toFixed(1);
    }
  }

  const isRunningLive = isStreaming && !hasAnswer && !isStopped;

  // Active live running status
  const activeStep =
    visibleSteps.slice().reverse().find((s) => s.status === "running") ||
    visibleSteps[visibleSteps.length - 1];

  const activeLiveStatus = activeStep
    ? formatToolDisplayName(activeStep, t, activeStep.status === "running" || isStreaming)
    : timelineGroups.length > 0 && timelineGroups[timelineGroups.length - 1].thought?.title
    ? timelineGroups[timelineGroups.length - 1].thought.title
    : (t ? t("assistant.thinking", "Thinking...") : "Thinking...");

  const stepUnit = renderedStepsCount === 1 ? "step" : "steps";

  const runningHeader = renderedStepsCount > 0
    ? t
      ? t("assistant.activityHeaderRunning", {
          seconds: elapsedSeconds,
          count: renderedStepsCount,
          defaultValue: `Working… ${elapsedSeconds}s · ${renderedStepsCount} ${stepUnit}`,
        })
      : `Working… ${elapsedSeconds}s · ${renderedStepsCount} ${stepUnit}`
    : t
    ? t("assistant.activityHeaderRunningNoSteps", {
        seconds: elapsedSeconds,
        defaultValue: `Working… ${elapsedSeconds}s`,
      })
    : `Working… ${elapsedSeconds}s`;

  const summaryLabel = formattedDuration
    ? t
      ? t("assistant.activityHeader", {
          seconds: formattedDuration,
          count: renderedStepsCount,
          defaultValue: `Worked for ${formattedDuration}s · ${renderedStepsCount} ${stepUnit}`,
        })
      : `Worked for ${formattedDuration}s · ${renderedStepsCount} ${stepUnit}`
    : t
    ? t("assistant.activityHeaderWithoutTime", {
        count: renderedStepsCount,
        defaultValue: `${renderedStepsCount} ${stepUnit}`,
      })
    : `${renderedStepsCount} ${stepUnit}`;

  const stoppedHeader = renderedStepsCount > 0
    ? t
      ? t("assistant.activityHeaderStoppedWithSteps", {
          count: renderedStepsCount,
          defaultValue: `Stopped · ${renderedStepsCount} ${stepUnit}`,
        })
      : `Stopped · ${renderedStepsCount} ${stepUnit}`
    : t
    ? t("assistant.activityHeaderStopped", { defaultValue: "Stopped" })
    : "Stopped";

  const headerLabel = isStopped
    ? stoppedHeader
    : isStreaming && renderedStepsCount === 0
    ? t
      ? t("assistant.activityHeaderRunningNoSteps", {
          seconds: elapsedSeconds,
          defaultValue: `Working… ${elapsedSeconds}s`,
        })
      : `Working… ${elapsedSeconds}s`
    : !showSteps && isRunningLive && activeStep
    ? activeLiveStatus
    : isStreaming && !turnElapsedMs
    ? runningHeader
    : summaryLabel;

  return (
    <div
      className="elelem-assistant-activity-timeline"
      data-testid="copilot-reasoning-accordion"
    >
      <button
        type="button"
        className="elelem-activity-header"
        onClick={handleToggle}
        aria-expanded={isOpen}
        data-testid="copilot-reasoning-toggle"
      >
        <span className="elelem-activity-header-left">
          {isRunningLive ? (
            <Loader2
              size={12}
              className="elelem-activity-header-icon elelem-assistant-spin"
              aria-hidden="true"
              data-testid="copilot-reasoning-spinner"
            />
          ) : (
            <Sparkles
              size={12}
              className="elelem-activity-header-icon"
              aria-hidden="true"
              data-testid="copilot-reasoning-icon"
            />
          )}
          <span className="elelem-activity-header-label">{headerLabel}</span>
        </span>
        <ChevronRight
          size={12}
          className={`elelem-activity-chevron ${isOpen ? "open" : ""}`}
          aria-hidden="true"
        />
      </button>

      <div
        className={`elelem-activity-body-wrapper ${isOpen ? "open" : "collapsed"}`}
        data-testid="copilot-reasoning-body"
      >
        <div className="elelem-activity-body-inner">
          {!hasItems && isStreaming && !isStopped ? (
            <div className="elelem-activity-group">
              <div className="elelem-activity-thought-container">
                <div className="elelem-activity-thought-row elelem-activity-shimmer">
                  <span className="elelem-activity-thought-prefix" aria-hidden="true">✦</span>
                  <span className="elelem-activity-thought-title">
                    {t ? t("assistant.thinking", "Thinking...") : "Thinking..."}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            timelineGroups.map((group) => (
              <div
                key={group.stepNum}
                className="elelem-activity-group"
                data-testid={`copilot-step-group-${group.stepNum}`}
              >
                {group.thought && (
                  <ActivityThoughtRow thought={group.thought} t={t} />
                )}
                {group.actions.map(({ step, globalIdx }) => (
                  <ActivityActionRow
                    key={step.tool_call_id || `action-${globalIdx}`}
                    step={step}
                    idx={globalIdx}
                    t={t}
                    isLive={isStreaming && globalIdx === visibleSteps.length - 1}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

AgentActivityTimeline.propTypes = {
  steps: PropTypes.array,
  thoughts: PropTypes.array,
  turnElapsedMs: PropTypes.number,
  isStreaming: PropTypes.bool,
  isStopped: PropTypes.bool,
  startedAt: PropTypes.number,
  hasAnswer: PropTypes.bool,
  showSteps: PropTypes.bool,
  t: PropTypes.func,
};

export default AgentActivityTimeline;
